import React, { createContext, PropsWithChildren, useState } from 'react';
import Dialog, { DialogOptions } from '@/components/@IntranetLibrary/Dialog';
import { uuidv4 } from '@/utils/function';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

type DialogType = {
  id?: string;
  isOpen: boolean;
  option: DialogOptions;
};
type ResultDialogType = DialogType | null;

export type DialogContextProps = {
  show: (options?: DialogOptions) => void;
  showError: (
    dialogOptions?: Omit<DialogOptions, 'message'> & { e?: unknown },
  ) => void;
  hide: (id?: string) => void;
  hideAll: () => void;
  stack: DialogType[];
};
const DialogContext = createContext<DialogContextProps | null>(null);
DialogContext.displayName = 'Dialog';

const DialogProvider = ({ children }: PropsWithChildren) => {
  const [dialogStacks, setDialogStacks] = useState<DialogType[]>([]);
  const { t } = useTranslation();

  const alertConfirmShow = (dialogOptions?: DialogOptions) => {
    const basicOption: DialogOptions = {
      type: 'alert',
      confirmButtonText: t('확인'),
      cancelButtonText: t('취소'),
    };
    if (
      dialogOptions &&
      !dialogOptions.type &&
      (!!dialogOptions.cancelButtonText || !!dialogOptions.onCancel)
    ) {
      dialogOptions.type = 'confirm';
    }

    setDialogStacks((prevState) =>
      prevState.filter((item) => item.id === dialogOptions?.id).length > 0
        ? [...prevState]
        : [
            ...prevState,
            {
              id: dialogOptions?.id || uuidv4(),
              isOpen: true,
              option: { ...basicOption, ...dialogOptions },
            },
          ],
    );
  };

  const alertConfirmShowError = (
    dialogOptions?: Omit<DialogOptions, 'message'> & { e?: unknown },
  ) => {
    if (dialogOptions) {
      const { e, ...otherOptions } = dialogOptions;
      let message = t('에러가 발생했습니다.');

      if (e instanceof AxiosError && e.response?.data?.message) {
        message = e.response.data.message;
      }

      alertConfirmShow({
        ...otherOptions,
        message,
      });
    }
  };

  const handleClose = (id?: string) => {
    if (id) {
      setDialogStacks((prevState) => {
        handleAfterClose(prevState, id);
        return prevState.filter((item) => item.id !== id);
      });
    } else {
      setDialogStacks((previousArr) => {
        handleAfterClose(previousArr, id);
        return previousArr.slice(0, -1);
      });
    }
  };

  const allClose = () => {
    setDialogStacks((prevState) => {
      prevState.forEach((item) => {
        handleAfterClose(prevState, item.id);
      });
      return [];
    });
  };

  const handleAfterClose = (stack: DialogType[], id: string | undefined) => {
    const target = dialogGet(stack, id) as ResultDialogType;
    if (target && target.option.onAfterClose) {
      target.option.onAfterClose();
    }
  };

  // id 를 주면 그 창만 — 없는 id 면 null. 예전엔 맨 위 창으로 대신해, 이미 닫힌 안내를
  // id 로 닫으려 할 때 다른 창의 onAfterClose 가 불렸다 (PORTAL-12584)
  const dialogGet = (stack: DialogType[], id: string | undefined) => {
    if (!stack.length) {
      return null;
    }
    if (id) {
      const result = stack.filter((item) => item.id === id);
      return result.length ? result[result.length - 1] : null;
    }
    return stack[stack.length - 1];
  };

  const handleClickConfirm = (dialog: DialogType) => {
    handleClose(dialog.id);
    dialog.option.onConfirm?.();
  };

  const handleClickCancel = (dialog: DialogType) => {
    handleClose(dialog.id);
    dialog.option.onCancel?.();
  };

  // id 를 주면 그 창만 닫는다 — 예전엔 id 를 무시하고 맨 위 창을 닫았다
  const handleHide = (id?: string) => handleClose(id);
  const handleHideAll = () => allClose();

  return (
    <>
      <DialogContext.Provider
        value={{
          show: alertConfirmShow,
          showError: alertConfirmShowError,
          hide: handleHide,
          hideAll: handleHideAll,
          stack: dialogStacks,
        }}
      >
        {children}
        {dialogStacks.map((dialog) => (
          <Dialog
            key={`dialog_${dialog.id}`}
            open={dialog.isOpen}
            onClose={handleClose}
            onClickCancel={() => handleClickCancel(dialog)}
            onClickConfirm={() => handleClickConfirm(dialog)}
            dialogOptions={dialog.option}
          />
        ))}
      </DialogContext.Provider>
    </>
  );
};

export { DialogProvider, DialogContext };
